import React, { useState, useRef, useEffect } from 'react';
import { uploadPhotos, convertToWebP } from '../../utils/photoService';

const UploadModal = ({ isOpen, onClose, onUploadSuccess }) => {
  // Each entry: { file, preview, id, title }
  // id is a stable unique key so duplicate filenames don't collide
  const [fileEntries, setFileEntries] = useState([]);
  const [baseTitle, setBaseTitle] = useState('');
  const [guestName, setGuestName] = useState('');
  const [usePerFileTitle, setUsePerFileTitle] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadPhase, setUploadPhase] = useState('');
  const [convertedSizes, setConvertedSizes] = useState({}); // id -> webp size in bytes
  const [error, setError] = useState('');

  const fileInputRef = useRef(null);

  // Revoke preview URLs on unmount or when entries change
  useEffect(() => {
    return () => {
      fileEntries.forEach(e => URL.revokeObjectURL(e.preview));
    };
  }, [fileEntries]);

  if (!isOpen) return null;

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files).map(file => ({
      file,
      preview: URL.createObjectURL(file),
      id: crypto.randomUUID(),
      title: '',
    }));
    setFileEntries(prev => [...prev, ...selected]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (id) => {
    setFileEntries(prev => {
      const entry = prev.find(e => e.id === id);
      if (entry) URL.revokeObjectURL(entry.preview);
      return prev.filter(e => e.id !== id);
    });
  };

  const handlePerFileTitleChange = (id, title) => {
    setFileEntries(prev => prev.map(e => e.id === id ? { ...e, title } : e));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (fileEntries.length === 0) {
      setError('Please select at least one photo.');
      return;
    }
    if (!guestName.trim()) {
      setError('Please enter your name.');
      return;
    }

    setIsUploading(true);
    setUploadPhase('optimizing');

    // Convert all files once, reuse results for both size display and upload
    const conversions = await Promise.all(fileEntries.map(e => convertToWebP(e.file)));
    const sizes = {};
    conversions.forEach(({ file: webpFile, converted: didConvert }, i) => {
      if (didConvert) sizes[fileEntries[i].id] = webpFile.size;
    });
    setConvertedSizes(sizes);
    setUploadPhase('uploading');

    try {
      await uploadPhotos(
        conversions.map(c => c.file),
        baseTitle,
        guestName,
        usePerFileTitle
          ? Object.fromEntries(fileEntries.map((e, i) => [conversions[i].file.name, e.title]))
          : {},
        null,
        conversions,
      );
      fileEntries.forEach(e => URL.revokeObjectURL(e.preview));
      setFileEntries([]);
      setBaseTitle('');
      setGuestName('');
      setUsePerFileTitle(false);
      setConvertedSizes({});
      onUploadSuccess();
      onClose();
    } catch (err) {
      console.error(err);
      setError('Failed to upload photos. Please try again.');
    } finally {
      setIsUploading(false);
      setUploadPhase('');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={!isUploading ? onClose : undefined}
      />

      {/* Modal Content */}
      <div className="relative bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl p-6 flex flex-col gap-4">
        <div className="flex justify-between items-center border-b pb-2">
          <h2 className="text-2xl font-['IBM_Plex_Serif'] text-black">Upload Photos</h2>
          {!isUploading && (
            <button onClick={onClose} className="text-gray-500 hover:text-black text-2xl">&times;</button>
          )}
        </div>

        {error && (
          <div className="bg-red-50 text-red-600 p-3 rounded text-sm text-center">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Your Name</label>
            <input
              type="text"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-blue"
              placeholder="e.g. John Doe"
              disabled={isUploading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Photos</label>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileChange}
              ref={fileInputRef}
              className="block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-full file:border-0
                file:text-sm file:font-semibold
                file:bg-blue file:text-white
                hover:file:bg-blue1"
              disabled={isUploading}
            />
          </div>

          {fileEntries.length > 0 && (
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{fileEntries.length} file(s) selected</span>
                <label className="flex items-center text-sm cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={usePerFileTitle}
                    onChange={(e) => setUsePerFileTitle(e.target.checked)}
                    className="mr-2"
                    disabled={isUploading}
                  />
                  Title per image
                </label>
              </div>

              {!usePerFileTitle && (
                <div className="mb-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Image Title / Header (Optional)</label>
                  <input
                    type="text"
                    value={baseTitle}
                    onChange={(e) => setBaseTitle(e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-blue"
                    placeholder="e.g. Fun at the reception (optional)"
                    disabled={isUploading}
                  />
                </div>
              )}

              {/* File List with Previews */}
              <div className="max-h-60 overflow-y-auto border rounded p-2 flex flex-col gap-2 bg-gray-50">
                {fileEntries.map((entry) => (
                  <div key={entry.id} className="flex flex-col gap-2 border-b last:border-0 pb-2">
                    <div className="flex items-center gap-3">
                      {/* Thumbnail */}
                      <div className="w-12 h-12 flex-shrink-0 bg-gray-200 rounded overflow-hidden border border-gray-300">
                        <img src={entry.preview} alt="preview" className="w-full h-full object-cover" />
                      </div>

                      {/* File Info */}
                      <div className="flex-1 min-w-0">
                        <p className="truncate text-sm font-medium text-gray-700">{entry.file.name}</p>
                        <p className="text-xs text-gray-500">
                          {(entry.file.size / 1024 / 1024).toFixed(2)} MB
                          {convertedSizes[entry.id] && (
                            <span className="text-green-600 ml-1">
                              → {(convertedSizes[entry.id] / 1024 / 1024).toFixed(2)} MB WebP
                            </span>
                          )}
                        </p>
                      </div>

                      {/* Remove Button */}
                      <button
                        type="button"
                        onClick={() => removeFile(entry.id)}
                        className="text-red-500 hover:text-red-700 p-1 hover:bg-red-50 rounded"
                        disabled={isUploading}
                        title="Remove image"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>

                    {/* Per File Title Input */}
                    {usePerFileTitle && (
                      <input
                        type="text"
                        value={entry.title}
                        onChange={(e) => handlePerFileTitleChange(entry.id, e.target.value)}
                        placeholder="Title for this image (optional)"
                        className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                        disabled={isUploading}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3 mt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 border border-gray-300 rounded-xl text-gray-700 font-serif italic hover:bg-gray-50 transition-all duration-300"
              disabled={isUploading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 py-3 bg-blue1 text-white rounded-xl font-serif italic hover:bg-blue transition-all duration-300 shadow-md disabled:opacity-70 flex justify-center items-center"
              disabled={isUploading}
            >
              {isUploading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {uploadPhase === 'optimizing' ? 'Optimizing...' : 'Uploading...'}
                </>
              ) : 'Upload'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UploadModal;
